import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { isBundledMobileApp } from '@/mobile/native/apiBase';
import { capFieldReportsList } from '@/mobile/sync/fieldCacheReportsCap';
import {
  clearFieldCacheMemory,
  peekFieldCacheMemory,
  setFieldCacheMemory,
} from '@/mobile/sync/fieldCacheMemory';
import { mergeFieldReportLists } from '@/mobile/sync/mergeFieldReportLists';
import { withoutCancelledFieldReports } from '@/modules/field/utils/fieldReportCancelled';
import { enqueuePreferencesWrite } from '@/mobile/sync/preferencesWriteQueue';
import {
  clearFieldListSqlite,
  deleteFieldReportsInSqlite,
  isFieldReportsSqliteActive,
  loadFieldProjectsFromSqlite,
  loadFieldReportsFromSqlite,
  replaceFieldProjectsInSqlite,
  replaceFieldReportsInSqlite,
  pruneFieldReportsInSqlite,
  upsertFieldReportInSqlite,
} from '@/mobile/sync/fieldSyncListSqlite';
import {
  clientUuidsResolvedByPushResults,
  fieldReportDayKey,
  outboxItemDayKey,
} from '@/mobile/sync/fieldSyncOutboxMatch';
import {
  appendOutboxItemToSqlite,
  loadOutboxFromSqlite,
  saveOutboxToSqlite,
} from '@/mobile/sync/fieldSyncOutboxSqlite';
import type {
  FieldBootstrapResponse,
  FieldSyncResponse,
} from '@/mobile/sync/fieldSyncTypes';
import { normalizeFieldReport } from '@/mobile/sync/normalizeFieldReport';
import type { FieldProjectListItem, FieldReportListItem } from '@/modules/field/types';
import { generateFieldId } from '@/modules/field/utils/generateId';

const CACHE_KEY = 'engetel_field_sync_cache_v2';
const CACHE_KEY_LEGACY = 'engetel_field_sync_cache_v1';
const OUTBOX_KEY = 'engetel_field_sync_outbox_v1';
const META_KEY = 'engetel_field_sync_meta_v1';
export const FIELD_SYNC_CACHE_SCHEMA = 4;

export type FieldSyncCache = {
  schemaVersion: number;
  cachedAt: string;
  lastPullAt: string | null;
  summary: FieldBootstrapResponse['summary'];
  projects: FieldProjectListItem[];
  reports: FieldReportListItem[];
  activities: FieldBootstrapResponse['activities'];
  /** APK: relatórios podem estar no SQLite (array vazio no Preferences). */
  reportsStorage?: 'inline' | 'sqlite';
};

export type FieldSyncOutboxItem = {
  id: string;
  createdAt: string;
  lastPushError?: string | null;
  lastPushAt?: string | null;
  operation: {
    entity: 'field_report';
    operation: 'create' | 'update' | 'delete';
    clientUuid: string;
    serverId?: number;
    version?: number;
    payload: Record<string, unknown>;
  };
};

type FieldSyncMeta = {
  deviceId: string;
  lastPullAt: string | null;
};

async function readJson<T>(key: string, fallback: T): Promise<T> {
  const { value } = await Preferences.get({ key });
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

async function writeJson(key: string, value: unknown): Promise<void> {
  await enqueuePreferencesWrite(() =>
    Preferences.set({ key, value: JSON.stringify(value) }),
  );
}

function shouldMirrorReportsToSqlite(): boolean {
  return isBundledMobileApp() && Capacitor.isNativePlatform();
}

async function hydrateCacheFromStorage(raw: FieldSyncCache): Promise<FieldSyncCache> {
  if (raw.reportsStorage === 'sqlite' && shouldMirrorReportsToSqlite()) {
    const reports = await loadFieldReportsFromSqlite();
    const projects =
      raw.projects.length > 0 ? raw.projects : await loadFieldProjectsFromSqlite();

    return {
      ...raw,
      reports,
      projects,
    };
  }

  if (
    shouldMirrorReportsToSqlite()
    && raw.reports.length > 0
    && !(await isFieldReportsSqliteActive())
  ) {
    await replaceFieldReportsInSqlite(capFieldReportsList(raw.reports));
    await replaceFieldProjectsInSqlite(raw.projects);

    const envelope: FieldSyncCache = {
      ...raw,
      reports: [],
      reportsStorage: 'sqlite',
    };

    await writeJson(CACHE_KEY, envelope);

    return {
      ...envelope,
      reports: capFieldReportsList(raw.reports),
      projects: raw.projects,
    };
  }

  return raw;
}

async function persistFieldCache(
  cache: FieldSyncCache,
  options: {
    mode?: 'full' | 'incremental';
    sqliteReports?: FieldReportListItem[];
  } = {},
): Promise<void> {
  const mode = options.mode ?? 'incremental';
  const reports = capFieldReportsList(cache.reports);
  const snapshot: FieldSyncCache = {
    ...cache,
    reports,
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
  };

  setFieldCacheMemory(snapshot);

  if (shouldMirrorReportsToSqlite()) {
    if (mode === 'full') {
      await replaceFieldReportsInSqlite(reports);
      await replaceFieldProjectsInSqlite(cache.projects);
    } else if (options.sqliteReports?.length) {
      for (const report of options.sqliteReports) {
        await upsertFieldReportInSqlite(report);
      }
    }

    if (mode === 'full' || options.sqliteReports?.length) {
      await pruneFieldReportsInSqlite(new Set(reports.map((report) => report.id)));
    }

    const envelope: FieldSyncCache = {
      ...snapshot,
      reports: [],
      reportsStorage: 'sqlite',
    };

    await writeJson(CACHE_KEY, envelope);

    return;
  }

  await writeJson(CACHE_KEY, {
    ...snapshot,
    reportsStorage: 'inline',
  });
}

export { peekFieldCacheMemory };

export async function getOrCreateDeviceId(): Promise<string> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  if (meta?.deviceId) {
    return meta.deviceId;
  }

  const deviceId = generateFieldId();
  await writeJson(META_KEY, { deviceId, lastPullAt: null } satisfies FieldSyncMeta);

  return deviceId;
}

export async function getLastPullAt(): Promise<string | null> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);

  return meta?.lastPullAt ?? null;
}

export async function setLastPullAt(iso: string): Promise<void> {
  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  const deviceId = meta?.deviceId ?? (await getOrCreateDeviceId());

  await writeJson(META_KEY, { deviceId, lastPullAt: iso } satisfies FieldSyncMeta);
}

async function purgeLegacyFieldCache(): Promise<void> {
  await Preferences.remove({ key: CACHE_KEY_LEGACY });
}

export async function loadFieldCache(options?: {
  forceStorage?: boolean;
}): Promise<FieldSyncCache | null> {
  if (!options?.forceStorage) {
    const memory = peekFieldCacheMemory();
    if (memory) {
      return memory;
    }
  }

  await purgeLegacyFieldCache();

  const raw = await readJson<FieldSyncCache | null>(CACHE_KEY, null);

  if (!raw) {
    return null;
  }

  if ((raw.schemaVersion ?? 1) < 3) {
    await clearFieldCache();
    return null;
  }

  const normalized: FieldSyncCache = {
    ...raw,
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
  };

  const hydrated = await hydrateCacheFromStorage(normalized);
  setFieldCacheMemory(hydrated);

  return hydrated;
}

function mergeListById<T extends { id: number }>(
  existing: T[],
  incoming: T[],
): T[] {
  if (incoming.length === 0) {
    return existing;
  }

  const map = new Map<number, T>();
  for (const item of existing) {
    map.set(item.id, item);
  }
  for (const item of incoming) {
    map.set(item.id, item);
  }

  return Array.from(map.values());
}

/** Grava snapshot completo (bootstrap). */
export async function saveFieldCache(data: FieldBootstrapResponse): Promise<void> {
  const payload: FieldSyncCache = {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: data.serverTime,
    summary: data.summary,
    projects: data.projects,
    reports: withoutCancelledFieldReports(
      data.reports.map((report) => normalizeFieldReport(report)),
    ),
    activities: data.activities,
  };

  await persistFieldCache(payload, { mode: 'full' });
  await setLastPullAt(data.serverTime);
}

/** Mescla delta da sync incremental sem apagar listas quando o servidor não envia alterações. */
export async function mergeFieldCacheDelta(
  data: FieldBootstrapResponse,
): Promise<void> {
  const existing =
    peekFieldCacheMemory() ??
    (await loadFieldCache({ forceStorage: true })) ?? {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: null,
    summary: data.summary,
    projects: [],
    reports: [],
    activities: [],
  };

  const hasProjectDelta = data.projects.length > 0;
  const hasReportDelta = data.reports.length > 0;
  const hasActivityDelta = data.activities.length > 0;

  const nextProjects = hasProjectDelta
    ? mergeListById(existing.projects, data.projects)
    : existing.projects;

  const nextReports = hasReportDelta
    ? withoutCancelledFieldReports(
        await mergeFieldReportLists(
          existing.reports,
          data.reports.map((r) => normalizeFieldReport(r)),
        ),
      )
    : withoutCancelledFieldReports(existing.reports);

  const nextActivities = hasActivityDelta ? data.activities : existing.activities;

  const summaryUnchanged =
    existing.summary.pending === data.summary.pending
    && existing.summary.recused === data.summary.recused
    && existing.summary.onTime === data.summary.onTime
    && existing.summary.delayed === data.summary.delayed;

  if (
    !hasProjectDelta
    && !hasReportDelta
    && !hasActivityDelta
    && summaryUnchanged
  ) {
    await setLastPullAt(data.serverTime);

    return;
  }

  const payload: FieldSyncCache = {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: data.serverTime,
    summary: data.summary,
    projects: nextProjects,
    reports: nextReports,
    activities: nextActivities,
  };

  await persistFieldCache(payload, {
    mode: 'incremental',
    sqliteReports: hasReportDelta
      ? data.reports.map((report) => normalizeFieldReport(report))
      : undefined,
  });
  await setLastPullAt(data.serverTime);
}

export async function clearFieldCache(): Promise<void> {
  clearFieldCacheMemory();
  await Preferences.remove({ key: CACHE_KEY });

  try {
    await clearFieldListSqlite();
  } catch (error) {
    console.warn('[field-cache] falha ao limpar SQLite de relatórios', error);
  }

  const meta = await readJson<FieldSyncMeta | null>(META_KEY, null);
  if (meta?.deviceId) {
    await writeJson(META_KEY, {
      deviceId: meta.deviceId,
      lastPullAt: null,
    } satisfies FieldSyncMeta);
  }
}

export async function loadOutbox(): Promise<FieldSyncOutboxItem[]> {
  if (Capacitor.isNativePlatform()) {
    return loadOutboxFromSqlite();
  }

  return readJson<FieldSyncOutboxItem[]>(OUTBOX_KEY, []);
}

export async function saveOutbox(items: FieldSyncOutboxItem[]): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await saveOutboxToSqlite(items);

    return;
  }

  await writeJson(OUTBOX_KEY, items);
}

export async function enqueueOutbox(
  operation: FieldSyncOutboxItem['operation'],
): Promise<void> {
  const item: FieldSyncOutboxItem = {
    id: generateFieldId(),
    createdAt: new Date().toISOString(),
    operation,
  };

  if (Capacitor.isNativePlatform()) {
    await appendOutboxItemToSqlite(item);

    return;
  }

  const outbox = await loadOutbox();
  outbox.push(item);
  await saveOutbox(outbox);
}

export async function clearOutbox(): Promise<void> {
  await saveOutbox([]);
}

export async function removeOutboxForClientUuids(
  clientUuids: string[],
): Promise<void> {
  if (clientUuids.length === 0) {
    return;
  }

  const keep = new Set(clientUuids);
  const outbox = await loadOutbox();

  await saveOutbox(
    outbox.filter((item) => !keep.has(item.operation.clientUuid)),
  );
}

export async function removeReportsFromFieldCache(options: {
  ids?: number[];
  clientUuids?: string[];
}): Promise<void> {
  const idSet = new Set(options.ids ?? []);
  const uuidSet = new Set(options.clientUuids ?? []);

  if (idSet.size === 0 && uuidSet.size === 0) {
    return;
  }

  const existing = await loadFieldCache();
  if (!existing) {
    return;
  }

  const reports = existing.reports.filter((report) => {
    if (report.clientUuid && uuidSet.has(report.clientUuid)) {
      return false;
    }

    if (report.id > 0 && idSet.has(report.id)) {
      return false;
    }

    return true;
  });

  if (reports.length === existing.reports.length) {
    return;
  }

  await deleteFieldReportsInSqlite({
    ids: Array.from(idSet),
    clientUuids: Array.from(uuidSet),
  });

  await persistFieldCache({
    ...existing,
    reports,
    cachedAt: new Date().toISOString(),
  });
}

/**
 * Upsert por id (sem remover outros cards do mesmo dia).
 * Use após aprovar RCP para injetar o RCP Equipe no cache do APK.
 */
export async function upsertFieldReportsIntoCache(
  incoming: FieldReportListItem[],
): Promise<void> {
  if (incoming.length === 0) {
    return;
  }

  const normalized = incoming.map((report) =>
    normalizeFieldReport({
      ...report,
      sync: 'synced',
      canSync: false,
    }),
  );

  const existing = await loadFieldCache();
  if (!existing) {
    await persistFieldCache({
      schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
      cachedAt: new Date().toISOString(),
      lastPullAt: null,
      summary: { pending: 0, recused: 0, onTime: 0, delayed: 0 },
      projects: [],
      reports: normalized,
      activities: [],
    });

    return;
  }

  const reports = await mergeFieldReportLists(existing.reports, normalized);

  await persistFieldCache(
    {
      ...existing,
      reports,
      cachedAt: new Date().toISOString(),
    },
    { mode: 'incremental', sqliteReports: normalized },
  );
}

/** Aplica RDOs confirmados pelo servidor (evita “limbo” na listagem). */
export async function mergeSyncedReportsIntoCache(
  incoming: FieldReportListItem[],
): Promise<void> {
  if (incoming.length === 0) {
    return;
  }

  const normalized = incoming.map((report) =>
    normalizeFieldReport({
      ...report,
      sync: 'synced',
      canSync: false,
    }),
  );

  const existing = await loadFieldCache();
  if (!existing) {
    await persistFieldCache({
      schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
      cachedAt: new Date().toISOString(),
      lastPullAt: null,
      summary: { pending: 0, recused: 0, onTime: 0, delayed: 0 },
      projects: [],
      reports: normalized,
      activities: [],
    });

    return;
  }

  const incomingUuids = new Set(
    normalized
      .map((report) => report.clientUuid)
      .filter((value): value is string => Boolean(value)),
  );

  const incomingDayKeys = new Set(
    normalized
      .map((report) => fieldReportDayKey(report.projectId, report.reportDateIso))
      .filter((value): value is string => Boolean(value)),
  );

  // Só remove rascunhos locais (not_synced) batendo por uuid/data.
  // Nunca apagar cards já sincronizados por dayKey — isso sumia com RCP Equipe
  // quando o pull trazia só outro relatório do mesmo projeto/dia.
  const withoutStalePending = existing.reports.filter((report) => {
    if (report.sync !== 'not_synced') {
      if (report.clientUuid && incomingUuids.has(report.clientUuid)) {
        return false;
      }

      return true;
    }

    if (report.clientUuid && incomingUuids.has(report.clientUuid)) {
      return false;
    }

    const dayKey = fieldReportDayKey(report.projectId, report.reportDateIso);
    if (dayKey && incomingDayKeys.has(dayKey)) {
      return false;
    }

    return true;
  });

  const reports = await mergeFieldReportLists(withoutStalePending, normalized);

  await persistFieldCache(
    {
      ...existing,
      reports,
      cachedAt: new Date().toISOString(),
    },
    { mode: 'incremental', sqliteReports: normalized },
  );
}

export async function recordOutboxPushErrors(
  response: FieldSyncResponse,
): Promise<void> {
  if (response.pushResults.length === 0) {
    return;
  }

  const errorsByUuid = new Map<string, string>();

  for (const result of response.pushResults) {
    // conflict + report já foi aplicado ao cache e removido do outbox.
    if (result.status === 'conflict' && result.report) {
      continue;
    }

    if (
      (result.status === 'error' || result.status === 'conflict') &&
      result.clientUuid
    ) {
      errorsByUuid.set(
        result.clientUuid,
        result.message ??
          (result.status === 'conflict'
            ? 'Conflito de versão no servidor.'
            : 'Falha ao enviar ao servidor.'),
      );
    }
  }

  if (errorsByUuid.size === 0) {
    return;
  }

  const outbox = await loadOutbox();
  const now = new Date().toISOString();
  let changed = false;

  const next = outbox.map((item) => {
    const message = errorsByUuid.get(item.operation.clientUuid);

    if (!message) {
      return item;
    }

    changed = true;

    return {
      ...item,
      lastPushError: message,
      lastPushAt: now,
    };
  });

  if (changed) {
    await saveOutbox(next);
  }
}

/**
 * Limpa a fila local quando o servidor já tem o RDO (ex.: enviado via /api/mobile/sync).
 */
export async function reconcileFieldOutboxWithServerReports(
  reports: FieldReportListItem[],
): Promise<number> {
  const outbox = await loadOutbox();

  if (outbox.length === 0 || reports.length === 0) {
    return 0;
  }

  const serverByUuid = new Map<string, FieldReportListItem>();
  const serverByDay = new Map<string, FieldReportListItem>();

  for (const report of reports) {
    const normalized = normalizeFieldReport({
      ...report,
      sync: 'synced',
      canSync: false,
    });

    if (report.clientUuid) {
      serverByUuid.set(report.clientUuid, normalized);
    }

    const dayKey = fieldReportDayKey(report.projectId, report.reportDateIso);
    if (dayKey) {
      serverByDay.set(dayKey, normalized);
    }
  }

  const confirmedUuids = new Set<string>();
  const reportsToMerge = new Map<string, FieldReportListItem>();

  for (const item of outbox) {
    const clientUuid = item.operation.clientUuid;
    const dayKey = outboxItemDayKey(item);
    const byUuid = serverByUuid.get(clientUuid);
    const byDay = dayKey ? serverByDay.get(dayKey) : undefined;
    const serverReport = byUuid ?? byDay;

    if (!serverReport) {
      continue;
    }

    confirmedUuids.add(clientUuid);
    reportsToMerge.set(clientUuid, serverReport);
  }

  if (confirmedUuids.size === 0) {
    return 0;
  }

  const confirmed = Array.from(confirmedUuids);

  await removeOutboxForClientUuids(confirmed);
  await removePendingReportsMatched(confirmed, Array.from(reportsToMerge.values()));
  await mergeSyncedReportsIntoCache(Array.from(reportsToMerge.values()));

  return confirmed.length;
}

export async function getOutboxPushError(clientUuid: string): Promise<string | null> {
  const outbox = await loadOutbox();

  for (let index = outbox.length - 1; index >= 0; index -= 1) {
    const item = outbox[index];

    if (item.operation.clientUuid === clientUuid && item.lastPushError) {
      return item.lastPushError;
    }
  }

  return null;
}

/** Remove cards pendentes já confirmados (por clientUuid ou mesmo projeto/data). */
async function removePendingReportsMatched(
  clientUuids: string[],
  syncedReports: FieldReportListItem[],
): Promise<void> {
  if (clientUuids.length === 0 && syncedReports.length === 0) {
    return;
  }

  const existing = await loadFieldCache();
  if (!existing) {
    return;
  }

  const confirmed = new Set(clientUuids);
  const dayKeys = new Set(
    syncedReports
      .map((report) => fieldReportDayKey(report.projectId, report.reportDateIso))
      .filter((value): value is string => Boolean(value)),
  );

  const reports = existing.reports.filter((report) => {
    if (report.sync !== 'not_synced') {
      return true;
    }

    if (report.clientUuid && confirmed.has(report.clientUuid)) {
      return false;
    }

    const dayKey = fieldReportDayKey(report.projectId, report.reportDateIso);
    if (dayKey && dayKeys.has(dayKey)) {
      return false;
    }

    return true;
  });

  if (reports.length === existing.reports.length) {
    return;
  }

  await persistFieldCache({
    ...existing,
    reports,
    cachedAt: new Date().toISOString(),
  });
}

export async function applyPushResultsFromSync(
  response: FieldSyncResponse,
): Promise<{ synced: number; failed: number }> {
  const outbox = await loadOutbox();
  const okUuids = clientUuidsResolvedByPushResults(response.pushResults, outbox);
  const syncedReports: FieldReportListItem[] = [];
  const deletedIds: number[] = [];
  const deletedUuids: string[] = [];

  for (const result of response.pushResults) {
    if (result.status === 'ok' || (result.status === 'conflict' && result.report)) {
      if (result.report) {
        // ok ou conflict com report do servidor (ex.: RCP já aprovado) — aceita canônico.
        syncedReports.push(
          normalizeFieldReport({
            ...result.report,
            sync: 'synced',
            clientUuid: result.clientUuid ?? result.report.clientUuid,
            canSync: false,
          }),
        );
      }

      if (Array.isArray(result.teamReports) && result.teamReports.length > 0) {
        for (const teamReport of result.teamReports) {
          syncedReports.push(
            normalizeFieldReport({
              ...teamReport,
              sync: 'synced',
              canSync: false,
            }),
          );
        }
      }

      if (!result.report) {
        if (result.serverId) {
          deletedIds.push(result.serverId);
        }

        if (result.clientUuid) {
          deletedUuids.push(result.clientUuid);
        }
      }

      continue;
    }
  }

  if (okUuids.length > 0) {
    await removeOutboxForClientUuids(okUuids);
    await removePendingReportsMatched(okUuids, syncedReports);
  }

  if (deletedIds.length > 0 || deletedUuids.length > 0) {
    await removeReportsFromFieldCache({
      ids: deletedIds,
      clientUuids: deletedUuids,
    });
  }

  if (syncedReports.length > 0) {
    await mergeSyncedReportsIntoCache(syncedReports);
  }

  await recordOutboxPushErrors(response);

  const synced = response.pushResults.filter(
    (result) =>
      result.status === 'ok' || (result.status === 'conflict' && result.report),
  ).length;
  const failed = response.pushResults.filter(
    (result) =>
      result.status === 'error' ||
      (result.status === 'conflict' && !result.report),
  ).length;

  return { synced, failed };
}

/** Inclui ou atualiza um RDO ainda não sincronizado na listagem em cache. */
export async function upsertPendingReportInCache(
  item: FieldReportListItem,
): Promise<void> {
  const existing =
    peekFieldCacheMemory() ??
    (await loadFieldCache({ forceStorage: true })) ?? {
    schemaVersion: FIELD_SYNC_CACHE_SCHEMA,
    cachedAt: new Date().toISOString(),
    lastPullAt: null,
    summary: { pending: 0, recused: 0, onTime: 0, delayed: 0 },
    projects: [],
    reports: [],
    activities: [],
  };

  const key = item.clientUuid ?? String(item.id);
  const reports = [
    item,
    ...existing.reports.filter((report) => {
      const reportKey = report.clientUuid ?? String(report.id);
      return reportKey !== key && report.id !== item.id;
    }),
  ];

  await persistFieldCache(
    {
      ...existing,
      reports,
      cachedAt: new Date().toISOString(),
    },
    { mode: 'incremental', sqliteReports: [item] },
  );
}

export async function updateFieldCacheReports(
  reports: FieldReportListItem[],
  sqliteReports?: FieldReportListItem[],
): Promise<void> {
  const existing =
    peekFieldCacheMemory() ??
    (await loadFieldCache({ forceStorage: true }));

  if (!existing) {
    return;
  }

  await persistFieldCache(
    {
      ...existing,
      reports,
      cachedAt: new Date().toISOString(),
    },
    {
      mode: 'incremental',
      sqliteReports,
    },
  );
}
